import UIButton from "@/components/buttons/UIButton";
import UIButtonProps from "@/components/buttons/UIButtonProps";
import UIText from "@/components/text/UIText";
import { delay } from "@/utils/functions";
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogProps, DialogTitle, styled } from "@mui/material";
import { useModal } from "mui-modal-provider";
import { FC, memo } from "react";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px",
  borderRadius: "16px",
  "& .MuiBackdrop-root": {
    backgroundColor: "rgba(12, 17, 29, 0.70)",
    backdropFilter: "blur(4px)",
  },
  "& .MuiPaper-root": {
    borderRadius: "10px",
  }
}));

const LogoContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "48px",
  height: "48px",
  border: "1px solid #EAECF0",
  borderRadius: "10px",
});

const Logo = styled("img")({
  width: "36px",
  height: "36px",
});

interface LogoProps {
  [key: string]: string;
}

const logos: LogoProps = {
  SMILE: "/assets/icons/face.svg",
  SAD: "/assets/icons/face-sad.svg",
  CONTENT: "/assets/icons/face-smile.svg",
};


type LogoKeys = "SMILE" | "SAD" | "CONTENT";

export interface UIGenericDialogProps extends DialogProps {
  actions?: UIButtonProps[];
  logo?: LogoKeys;
  titleContent?: React.ReactNode;
  message?: React.ReactNode;
  width?: string | number;
}



const UIGenericDialog: FC<UIGenericDialogProps> = ({
  actions,
  logo,
  titleContent,
  message,
  title,
  width,
  ...props
}) => {

  const onClosePreventBackdropClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    if (props.onClose) props.onClose(event, reason);
  }

  const { hideModal } = useModal();

  return (
    <StyledDialog
      id={props.id}
      disableEnforceFocus
      disableEscapeKeyDown
      onClose={onClosePreventBackdropClick}
      {...props}
      sx={{ padding: "40px", borderRadius: "16px", ...props.sx }}
    >
      <Box sx={{ maxWidth: width || "440px", padding: 2 }}>
        <DialogTitle>
          {logo &&
            <LogoContainer>
              <Logo src={logos[logo]} alt="Logo" />
            </LogoContainer>
          }

          {title && (
            <DialogContentText sx={{ pt: 2 }}>
              <UIText text={title} color="blue" size="text-xl" weight="bold" />
            </DialogContentText>
          )}

          {titleContent}
        </DialogTitle>

        <DialogContent>
          {message}
        </DialogContent>

        {actions && actions.length > 0 && (
          <DialogActions>
            {actions.map((action, index) => (
              <UIButton
                key={index}
                label={action.label}
                color={action.color}
                startIcon={action.startIcon}
                endIcon={action.endIcon}
                sx={action.sx}
                disabled={action.disabled}
                fullWidth={action.fullWidth}
                onClick={(e) => {
                  if (props.id) {
                    hideModal(props.id);
                  }

                  if (props.onClose) {
                    props.onClose({}, "backdropClick");
                  }

                  if (action.onClick)
                    action.onClick(e);
                }}
              />
            ))}
          </DialogActions>
        )}
      </Box>
    </StyledDialog>
  );
};

UIGenericDialog.displayName = "UIGenericDialog";
export default memo(UIGenericDialog);
